import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useMsal} from "@azure/msal-react";
import {AccountInfo} from "@azure/msal-common";
import {IToken} from "@models/IToken.ts";
import dayjs from "dayjs";
import type {IPermissao, PermissaoAplicacao} from "@models/seguranca/permissaoApp.ts";
import {_SegurancaEndpoints} from "@localization/siteMap/endpoints/_SegurancaEndpoints.ts";
import {Access, AuthContext} from "@contexts/AuthContext.tsx";
import {useApi} from "@contexts/hooks/api/useApi.ts";
import {IUsuario} from "@models/conta/IUsuario.ts";
import {useApiPromises} from "@contexts/hooks/api/useApiPromises.ts";

export const AuthProvider = ({children}: { children: React.ReactNode }) => {
	const {instance, accounts} = useMsal();
	const {Get} = useApi()
	const [msalAccount, setMsalAccount] = useState<AccountInfo | null>(null);
	const [token, setToken] = useState<IToken>({token: '', expira: dayjs()});
	const [permissoes, setPermissoes] = useState<IPermissao[]>([]);
	const [userData, setUserData] = useState<IUsuario | undefined>(undefined);
	const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);

	const apiPromises = useApiPromises()

	const requestToken = async () => {
		return await instance.acquireTokenSilent({
			scopes: ["api://cb2ead97-d3cf-41c6-a3cd-34dd615bddf9/User.Read"],
		}).then((response) => {
			return {
				token: response.accessToken,
				expira: dayjs(response.expiresOn),
				recebido: dayjs(),
			}
		}).then(setToken)
	};

	const refreshPermissoes = useCallback(async () => {
		await Get(_SegurancaEndpoints.getMinhasPermissoes({page: 1, pageSize: 10000})).then(resp => {
			if (resp.status == 'Success') {
				setPermissoes(resp.items);
			}
		});
	}, [userData, token, msalAccount])

	const refreshUserData = useCallback(async () => {
		await Get(_SegurancaEndpoints.isAutorizado()).then(resp => {
			if (resp.items != null)
				setUserData({...userData, ...resp.items});
			else {
				setUserData(undefined)
			}
		});
	}, [token, msalAccount])

	const refreshToken = async () => {
		if (refreshInterval != null) clearInterval(refreshInterval);
		if (refreshInterval == null) {
			setRefreshInterval(setInterval(async () => {
				await requestToken().then(refreshUserData).then(refreshPermissoes)
			}, 1000 * 60 * 25))
		}

		return await requestToken().then(refreshUserData).then(refreshPermissoes)
	}

	useEffect(() => {
		if (accounts.length == 0) {
			return
		}
		instance.setActiveAccount(accounts[0])
		setMsalAccount(accounts[0])
	}, [accounts]);

	useEffect(() => {
		if (msalAccount == null) return;

		requestToken().then()
	}, [msalAccount]);

	useEffect(() => {
		if (!token || token.token == '') return;
		if (token.expira < dayjs()) {
			sessionStorage.removeItem('token');
		}

		if (msalAccount == null) return;
		refreshUserData().then(refreshPermissoes)

		sessionStorage.setItem('token', JSON.stringify(token));
	}, [token, msalAccount, userData == null]);

	const checkAccess = (permissao: PermissaoAplicacao): Access => {
		if (!permissoes || permissoes.length == 0) return false;
		if (permissoes.some(x => x.nome == permissao)) return true;

		const checkParentPermissions = (permission: IPermissao | undefined): boolean => {
			if (!permission || !permission.permissaoPai) return false;
			if (permission.permissaoPai.nome == permissao) return true;
			return checkParentPermissions(permission?.permissaoPai);
		};

		if (permissoes.some(checkParentPermissions)) return 'indirect';

		return false;
	};

	const hasAccess = useCallback((permissoes: PermissaoAplicacao[] | PermissaoAplicacao): Access => {
		if (typeof permissoes == 'string') {
			return checkAccess(permissoes);
		}

		return permissoes.some(x => checkAccess(x) == true || checkAccess(x) == 'indirect');
	}, [permissoes]);

	// const setWrongToken = () => {
	// 	setToken({
	// 		token: "eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiIsIng1dCI6InlFVXdtWFdMMTA3Q2MtN1FaMldTYmVPYjNzUSIsImtpZCI6InlFVXdtWFdMMTA3Q2MtN1FaMldTYmVPYjNzUSJ9.eyJhdWQiOiJhcGk6Ly9jYjJlYWQ5Ny1kM2NmLTQxYzYtYTNjZC0zNGRkNjE1YmRkZjkiLCJpc3MiOiJodHRwczovL3N0cy53aW5kb3dzLm5ldC8xZWRiM2FhYi00N2RiLTQzNzQtYjM4MC03ZjYyYzk3MGVmOTEvIiwiaWF0IjoxNzYyMjY1NjAzLCJuYmYiOjE3NjIyNjU2MDMsImV4cCI6MTc2MjI2OTczOCwiYWNyIjoiMSIsImFpbyI6IkFYUUFpLzhhQUFBQTdhL3BBcE1WN1ZnSjJEcktvRmdJODM3cUU4dFR0V3V6MGRWL1ExTUgxWVBPZG84TEUxZ1JsS1ozL2RkSWVzWHZOVHNIOEN5L1hBYzZJdE9CcHpOUjBJOVgzUnNCZmhNdTB6b1d5dGFMUjlpTDBlZDljbHFYOUdjYWdZZ25oNzgzTDNYRHp5RDF6TkpuRm1kbUlrZEFoZz09IiwiYW1yIjpbInB3ZCIsIm1mYSJdLCJhcHBpZCI6ImNiMmVhZDk3LWQzY2YtNDFjNi1hM2NkLTM0ZGQ2MTViZGRmOSIsImFwcGlkYWNyIjoiMCIsImZhbWlseV9uYW1lIjoiQ2FyZG9zbyBNZW5kZXMiLCJnaXZlbl9uYW1lIjoiQ3Jpc3RpYW4iLCJpcGFkZHIiOiIyODA0OjdjMDo5MjFiOjYwMDo1ODVjOmM0YjU6MmQ0ZjpjZDJlIiwibmFtZSI6IkNyaXN0aWFuIENhcmRvc28gTWVuZGVzIiwib2lkIjoiYmQxOWU3OWEtNDI2OS00YjU5LTk3Y2YtNTgzMDViODI2NTUyIiwib25wcmVtX3NpZCI6IlMtMS01LTIxLTk0Mzg3NDQ1Ny0xNzU5ODk5MzYzLTM1MTk5MTc1MDEtNTE3MiIsInJoIjoiMS5BWFlBcXpyYkh0dEhkRU96Z0g5aXlYRHZrWmV0THN2UDA4WkJvODAwM1dGYjNmbDJBSmgyQUEuIiwic2NwIjoiVXNlci5SZWFkIiwic2lkIjoiMDAzMDhkYzktMmFmYy04MzE2LWE5MjEtMmFlMzU3MmRjMjM0Iiwic3ViIjoiY2cySzRIZklyM2JlYXp1emJuaU85c3plLTZMdUh5ZElrZ0UyTEFQem04VSIsInRpZCI6IjFlZGIzYWFiLTQ3ZGItNDM3NC1iMzgwLTdmNjJjOTcwZWY5MSIsInVuaXF1ZV9uYW1lIjoiY3Jpc3RpYW4ubWVuZGVzQGxpZGVyYXRhY2FkaXN0YS5jb20uYnIiLCJ1cG4iOiJjcmlzdGlhbi5tZW5kZXNAbGlkZXJhdGFjYWRpc3RhLmNvbS5iciIsInV0aSI6IjdFOFlwSmNyQTBpY2lsR25lMGl5QUEiLCJ2ZXIiOiIxLjAiLCJ4bXNfZnRkIjoidnZHaVFOVGo2cEFTMkFKRFQ5dUxnQVc0OFowLTJBNXpvcFNBYTJiTFdSY0JkWE56YjNWMGFDMWtjMjF6In0.d6NA61etA3t0jqs2skpWd1JanIpQdwLrcM_Q3hqKevnQfQ5Mv0EKNK4oQCtTnQx_f0FGBOrBYGQpQ9fUFtqzqTuBlDW_PoxENLrTyWt6MHWrOClZpbpEomU28dlQ8OFqogUkjGs2WGQe_grk-BBXyTo7DJcMhajcVDEMddgbrpShkqNL8tYK-vnfNtSX4UwkhfvIMN-gEOsGqNSLMdM_fBWzKwuojK68-B44KImusQMmWTOvS_r1iGNpYDFKdr7MF6TQOGaBBdX6OCSS7X3bcNpfGT4In6eoojDds5Cx8gNoGzF_MSdIGqlQNlfMR10NiWDJlGpq7rGh0GfwTr9oIg",
	// 		expira: dayjs("2025-11-04T15:22:10.000Z"),
	// 		recebido: dayjs("2025-11-04T14:18:16.693Z")
	// 	})
	// }

	const tokenExpired = useMemo(() => {
			return token.expira.isBefore(dayjs())
		},
		[token])

	// Quando tiver um erro nos gets, e o erro for 401, verifica se o token já está expirado,
	// se sim, faz o refresh do token
	useEffect(() => {
		if (apiPromises.length == 0) return;
		apiPromises[apiPromises.length - 1].then(x => {
			if (x.status == 401) {
				if (tokenExpired || userData == null) {
					refreshToken().then()
				}
			}
		})
	}, [apiPromises]);


	return (<AuthContext.Provider
			value={{
				conta: msalAccount ?? undefined,
				token,
				userData,
				hasAccess,
				permissoes,
				refreshData: refreshToken
			}}>
			{children}
		</AuthContext.Provider>
	);
};